import type { ReactNode } from "react";

type Props = {
  /** Two-digit editorial index, e.g. "01". Optional. */
  index?: string;
  /** Mono eyebrow label, rendered after a `//` token. */
  label: string;
  /** Heading content — allows mixed-weight spans. */
  heading: ReactNode;
  /** Optional right-aligned action (e.g. an "All posts" link). */
  action?: ReactNode;
  /** Heading tag. Defaults to h2. */
  as?: "h1" | "h2";
  /** Extra classes on the heading element. */
  headingClassName?: string;
};

/**
 * Editorial section header: a mono kicker ("01 // Label") on a hairline rule,
 * above an oversized Space Grotesk display heading. Shared across all sections
 * so the eyebrow + heading rhythm stays identical site-wide.
 */
export default function SectionHeader({
  index,
  label,
  heading,
  action,
  as = "h2",
  headingClassName = "",
}: Props) {
  const Heading = as;

  return (
    <div>
      <div className="flex items-center gap-4 mb-5">
        <span className="kicker shrink-0">
          {index && <span className="text-[var(--muted)]">{index}&nbsp;</span>}
          <span className="text-[var(--muted)]">{"//"}</span> {label}
        </span>
        <span aria-hidden="true" className="h-px flex-1 bg-white/10" />
        {action && <span className="shrink-0">{action}</span>}
      </div>
      <Heading
        className={`font-display text-4xl md:text-5xl 2xl:text-6xl font-light text-[var(--text)] leading-[1.05] ${headingClassName}`}
      >
        {heading}
      </Heading>
    </div>
  );
}
